import type { Block, HelpPage, Inline } from './pages.js';

/**
 * Finding a task by the words a person uses (docs/HELP.md, criterion 6).
 *
 * Small and local, on purpose: the pages ship with the POS, so there is no
 * service to run and it works with the shop's wifi down. Every word typed must
 * match somewhere — a word, or the start of one, so results arrive while
 * typing — and a match in the title counts most, then the words people use
 * for the task, then its summary, then its steps.
 */

const WEIGHTS = { title: 8, synonyms: 6, summary: 3, body: 1 } as const;

/** Lower-case words, without punctuation; a plural matches its singular. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[’']/g, '')
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '')
    .map((word) =>
      word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word,
    );
}

function inlineText(parts: readonly Inline[]): string {
  return parts.map((part) => (part.kind === 'link' ? '' : part.text)).join(' ');
}

function blockText(block: Block): string {
  switch (block.kind) {
    case 'heading':
      return block.text;
    case 'paragraph':
      return inlineText(block.content);
    case 'steps':
    case 'list':
      return block.items.map(inlineText).join(' ');
  }
}

export interface Indexed {
  readonly page: HelpPage;
  readonly fields: Readonly<Record<keyof typeof WEIGHTS, readonly string[]>>;
}

export function indexPages(pages: readonly HelpPage[]): Indexed[] {
  return pages.map((page) => ({
    page,
    fields: {
      title: words(page.title),
      synonyms: page.synonyms.flatMap(words),
      summary: words(page.summary),
      body: page.blocks.flatMap((block) => words(blockText(block))),
    },
  }));
}

/** How well one typed word matches a field: whole word beats a word's start. */
function matchIn(field: readonly string[], typed: string): number {
  let best = 0;
  for (const word of field) {
    if (word === typed) return 1;
    if (word.startsWith(typed)) best = Math.max(best, 0.6);
  }
  return best;
}

/**
 * The pages that match every word typed, best first. An empty query finds
 * nothing — the panel shows its sections then, not a list of everything.
 */
export function searchPages(index: readonly Indexed[], query: string, limit = 12): HelpPage[] {
  const typed = words(query);
  if (typed.length === 0) return [];

  const scored: { page: HelpPage; score: number }[] = [];
  for (const entry of index) {
    let score = 0;
    let everyWord = true;
    for (const word of typed) {
      let wordScore = 0;
      for (const field of Object.keys(WEIGHTS) as (keyof typeof WEIGHTS)[]) {
        wordScore += matchIn(entry.fields[field], word) * WEIGHTS[field];
      }
      if (wordScore === 0) {
        everyWord = false;
        break;
      }
      score += wordScore;
    }
    if (everyWord) scored.push({ page: entry.page, score });
  }
  return scored
    .sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title))
    .slice(0, limit)
    .map((entry) => entry.page);
}
